import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Box, DialogContent, Grid, LinearProgress } from '@mui/material';
import Container from '@mui/material/Container';
import styles from '../Common/LabelSeparation/labelSeparation.styles';
import {
  getBrazilLabelSeparationDetail,
  resolveBrazilLabelSeparationDetail,
  saveBrazilLabelSeparation
} from '../../api/brazil';
import { resolveLabelSeparationSaveResponse } from '../../api/labelSeparation';
import LabelRangeForm from '../Common/LabelSeparation/LabelRangeForm';
import LabelSeparationDialogHeader from '../Common/LabelSeparation/LabelSeparationDialogHeader';
import {
  createEmptyRange,
  validateAndParseRanges,
  variantsToDrafts
} from '../Common/LabelSeparation/labelRangeLogic';
import SignedPdfViewerPane from '../Common/LabelSeparation/SignedPdfViewerPane';
import { LabelRangeDraft, LabelSeparationDetail } from '../Common/LabelSeparation/types';
import Actions from '../../store/actions';
import Store from '../../store';

type BrazilLabelSeparationDialogProps = {
  documentId: number;
  onClose: () => void;
  onSaved: () => void;
  /* eslint-disable no-unused-vars -- type-only callback signature */
  onSavingChange?: (saving: boolean) => void;
  /* eslint-enable no-unused-vars */
};

const BrazilLabelSeparationDialog = ({
  documentId,
  onClose,
  onSaved,
  onSavingChange
}: BrazilLabelSeparationDialogProps) => {
  const { dispatch } = useContext<any>(Store);
  const [detailLoading, setDetailLoading] = useState(true);
  const [detail, setDetail] = useState<LabelSeparationDetail | null>(null);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [ranges, setRanges] = useState<LabelRangeDraft[]>([createEmptyRange()]);
  const [saving, setSaving] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      onSavingChange?.(false);
    };
  }, [onSavingChange]);

  useEffect(() => {
    onSavingChange?.(saving);
  }, [onSavingChange, saving]);

  const handleClose = useCallback(() => {
    if (saving) {
      return;
    }
    onClose();
  }, [onClose, saving]);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        setDetailLoading(true);
        const response = await getBrazilLabelSeparationDetail(documentId);
        const result = resolveBrazilLabelSeparationDetail(response);

        if (!result.ok) {
          await dispatch({
            type: Actions.SET_ALERT,
            value: { status: true, message: result.message }
          });
          onClose();
          return;
        }

        const url = result.data.en_url ?? result.data.pt_url;
        if (!url) {
          await dispatch({
            type: Actions.SET_ALERT,
            value: { status: true, message: 'No PDF available for this document' }
          });
          handleClose();
          return;
        }

        if (!isMountedRef.current) {
          return;
        }

        setDetail(result.data);
        setRanges(variantsToDrafts(result.data.variants));
        setSignedUrl(url);
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to load label separation';
        await dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message }
        });
        handleClose();
      } finally {
        if (isMountedRef.current) {
          setDetailLoading(false);
        }
      }
    };

    fetchDetail();
  }, [documentId, dispatch, handleClose]);

  const handleAddRange = useCallback(() => {
    setRanges(current => [...current, createEmptyRange()]);
  }, []);

  const handleRemoveRange = useCallback((id: string) => {
    setRanges(current => {
      if (current.length <= 1) {
        return current;
      }
      return current.filter(range => range.id !== id);
    });
  }, []);

  const handleRangeChange = useCallback(
    (id: string, field: keyof Omit<LabelRangeDraft, 'id'>, value: string) => {
      setRanges(current =>
        current.map(range => (range.id === id ? { ...range, [field]: value } : range))
      );
    },
    []
  );

  const handleSave = useCallback(async () => {
    const validation = validateAndParseRanges(ranges, detail?.page_count ?? null);
    if (!validation.ok) {
      await dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message: validation.message }
      });
      return;
    }

    setSaving(true);
    try {
      const response = await saveBrazilLabelSeparation(documentId, { ranges: validation.payload });
      const result = resolveLabelSeparationSaveResponse(response);

      if (!result.ok) {
        await dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message: result.message }
        });
        return;
      }

      await dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message: result.message, color: 'success' }
      });
      onSaved();
      onClose();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to save label separation';
      await dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message }
      });
    } finally {
      if (isMountedRef.current) {
        setSaving(false);
      }
    }
  }, [detail?.page_count, dispatch, documentId, onClose, onSaved, ranges]);

  return (
    <Container maxWidth={false}>
      <LabelSeparationDialogHeader
        title='Multi Label Separation'
        subtitle={detail?.registration_number}
        closeDisabled={saving}
        onClose={handleClose}
      />

      {detailLoading ? (
        <Box sx={styles.loadingBox}>
          <LinearProgress color='secondary' />
        </Box>
      ) : (
        <DialogContent sx={styles.dialogContent}>
          <Grid container spacing={2} sx={styles.gridContainer}>
            <Grid item xs={12} lg={7} sx={styles.pdfColumn}>
              <SignedPdfViewerPane loading={detailLoading} pdfUrl={signedUrl} height='100%' />
            </Grid>
            <Grid item xs={12} lg={5} sx={styles.formColumn}>
              <LabelRangeForm
                ranges={ranges}
                saving={saving}
                onAddRange={handleAddRange}
                onRemoveRange={handleRemoveRange}
                onRangeChange={handleRangeChange}
                onSave={handleSave}
                onClose={handleClose}
              />
            </Grid>
          </Grid>
        </DialogContent>
      )}
    </Container>
  );
};

BrazilLabelSeparationDialog.defaultProps = {
  onSavingChange: undefined
};

export default BrazilLabelSeparationDialog;
